import {useQuery} from '@apollo/client';
import TableCell from '@material-ui/core/TableCell/TableCell';
import TableRow from '@material-ui/core/TableRow/TableRow';
import Skeleton from '@material-ui/lab/Skeleton';
import {GET_TOURNAMENTS} from 'packages/graphql/v2/queries';
import {Tournament} from 'packages/common-types/schema-types';
import {TOURNAMENT_LIST_TABLE_HEADERS} from 'packages/constants/table-constants';
import {useModal} from 'packages/hooks/use-modal';
import BaseTable from 'packages/tables/BaseTable';
import {useState, useEffect, memo} from 'react';
import TableMenu from 'packages/common-ui/TableMenu';
import MenuItem from '@material-ui/core/MenuItem';
import EditTournament from 'packages/dialogs/EditTournament';
import ConfirmAction from 'packages/dialogs/ConfirmAction';
import useTournament from 'packages/hooks/use-tournament';
import Swal from 'sweetalert2';

type GetTournamentData = {
  getTournaments: Tournament[];
};

const TournamentsTable = memo(() => {
  const {
    data,
    loading,
    refetch: refectTournaments,
  } = useQuery<GetTournamentData>(GET_TOURNAMENTS);
  const {toggle: toggleAlert, modalOpen: alertOpen} = useModal();
  const {toggle: toggleEdit, modalOpen: modalEditOpen} = useModal();
  const {toggle: toggleDelete, modalOpen: isOpenDelete} = useModal();
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const {deleteTournament, startTournament, isLoading} = useTournament();

  useEffect(() => {}, [data]);

  const handleStartTournament = async () => {
    try {
      await startTournament({tournamentId: selectedTournamentId});
      toggleAlert();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteTournament = () => {
    try {
      deleteTournament({tournamentId: selectedTournamentId});
      Swal.fire({
        title: 'Hecho!',
        text: 'El torneo ha sido eliminado correctamente.',
        icon: 'success',
      });
      toggleDelete();
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'Error!',
        text: 'Ha ocurrido un error al eliminar un torneo.',
        icon: 'error',
      });
    }
  };

  if (!data || loading) {
    return (
      <div style={{marginTop: 30}}>
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
        <Skeleton variant="rect" height={38} />
      </div>
    );
  }

  return (
    <>
      <ConfirmAction
        modalOpen={isOpenDelete}
        toggle={toggleDelete}
        title="Eliminar torneo"
        loading={isLoading}
        onConfirm={handleDeleteTournament}
        description="¿Estas seguro que deseas eliminar este torneo?"
      />
      <ConfirmAction
        modalOpen={alertOpen}
        toggle={toggleAlert}
        title="Iniciar torneo"
        loading={isLoading}
        onConfirm={handleStartTournament}
        description="¿Estas seguro que deseas iniciar este torneo? Una vez lo hagas los usuarios no podran volver a preinscribirse"
      />
      <EditTournament
        toggle={toggleEdit}
        modalOpen={modalEditOpen}
        tournamentId={selectedTournamentId}
        refectTournaments={refectTournaments}
      />
      <BaseTable headers={TOURNAMENT_LIST_TABLE_HEADERS}>
        {data?.getTournaments.map((tournament) => {
          const {
            _id: id,
            conditions,
            hasFinal,
            name,
            status,
            inscriptions,
          } = tournament;
          return (
            <TableRow key={id}>
              <TableCell component="th" scope="row">
                {name}
              </TableCell>
              <TableCell align="center">{conditions.tournamentType}</TableCell>
              <TableCell align="center">{conditions.gender}</TableCell>
              <TableCell align="center">{conditions.modalityGame}</TableCell>
              <TableCell align="center">
                {conditions.tournamentCategory}
              </TableCell>
              <TableCell align="center">{conditions.numberOfRounds}</TableCell>
              <TableCell align="center">{conditions.pointLimit}</TableCell>
              <TableCell align="center">{hasFinal ? 'Si' : 'No'}</TableCell>
              <TableCell align="center">{status}</TableCell>
              <TableCell align="center">{inscriptions.length}</TableCell>
              <TableCell align="center">
                <TableMenu>
                  <MenuItem
                    disabled={
                      status === 'Iniciado' ||
                      inscriptions.length % 4 !== 0 ||
                      !inscriptions.length
                    }
                    onClick={() => {
                      setSelectedTournamentId(id);
                      toggleAlert();
                    }}
                  >
                    {status === 'Iniciado' ? 'Torneo Iniciado' : 'Empezar'}
                  </MenuItem>
                  <MenuItem
                    disabled={status === 'Iniciado'}
                    onClick={() => {
                      setSelectedTournamentId(id);
                      toggleEdit();
                    }}
                  >
                    Editar torneo
                  </MenuItem>
                  <MenuItem
                    disabled={status === 'Iniciado'}
                    onClick={() => {
                      setSelectedTournamentId(id);
                      toggleDelete();
                    }}
                  >
                    Eliminar torneo
                  </MenuItem>
                </TableMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </BaseTable>
    </>
  );
});

TournamentsTable.displayName = `TournamentsTable`;

export default TournamentsTable;
